import { useState } from 'react';
import { NJButton, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import { DecisionNote } from './DecisionPanel';
import { ConfirmDialog } from './ConfirmDialog';
import type { Studio } from './hooks';

export function SyncPanel({ studio, onDecision }: { studio: Studio; onDecision: (id: string) => void }) {
  const [confirm, setConfirm] = useState(false);
  const { state } = studio;
  const affectedPlans = (id: string) => state.plans.filter(p => id in p.subscribers);
  const affectedNames = (id: string) => affectedPlans(id).map(p => p.name).join(', ') || 'aucun plan';
  return <section className="sp-panel sp-stack">
    <NJText as="div" className="pu-label">Copie du 16 octobre 2026 · comparaison simulée</NJText>
    <NJHeading as="h2" scale="md">Évolutions UrbanMap</NJHeading>
    <NJText>3 écarts de démonstration : 1 modification de surface, 2 suppressions.
      Aucun appel à UrbanMap. Toutes les corrections locales sont conservées.</NJText>
    <article className="pu-card sp-stack">
      <NJHeading as="h3" scale="sm">École Henri-Barbusse · c1</NJHeading>
      <NJText>Surface chauffée : {state.synced ? 'mise à jour de +10 % appliquée' : `${state.buildings[0].surface} → ${Math.round(state.buildings[0].surface * 1.1)} m²`}.</NJText>
      <NJText scale="sm">Impact : consommation calculée modifiée. Correction finale éventuelle inchangée.
        Les résultats existants des plans concernés sont conservés et signalés à recalculer.</NJText>
    </article>
    <article className="pu-card sp-stack">
      <NJHeading as="h3" scale="sm">Résidence des Acacias · c2</NJHeading>
      <NJText>Présent dans la copie → supprimé dans UrbanMap. Utilisé dans {affectedPlans('c2').length} plan(s) : {affectedNames('c2')}.</NJText>
      <NJText>Copie et abonnements conservés. {state.synced && state.buildings.find(b => b.id === 'c2')?.conflict ?
        'Conflit enregistré.' : 'Le conflit sera signalé après confirmation.'}</NJText>
      <DecisionNote id="conflict" onOpen={onDecision} />
    </article>
    <article className="pu-card sp-stack">
      <NJHeading as="h3" scale="sm">Médiathèque de Gentilly · c6</NJHeading>
      <NJText>Présente dans la copie → supprimée dans UrbanMap. Utilisée dans {affectedPlans('c6').length} plan(s) : {affectedNames('c6')}.</NJText>
      <NJText>Copie et abonnements conservés. {state.synced && state.buildings.find(b => b.id === 'c6')?.conflict ?
        'Conflit enregistré.' : 'Le conflit sera signalé après confirmation.'}</NJText>
      <DecisionNote id="unused" onOpen={onDecision} />
    </article>
    <NJButton label={state.synced ? 'Modifications applicables déjà synchronisées' : 'Examiner puis synchroniser'}
      disabled={state.synced} icon="sync" onClick={() => setConfirm(true)} />
    {state.synced && <div className="sp-warning" role="status">
      <NJText>Synchronisation partielle effectuée : surface mise à jour, corrections conservées.
        Les deux suppressions ne sont pas résolues. Le projet n’est pas annoncé entièrement synchronisé.</NJText>
    </div>}
    <DecisionNote id="targeting" onOpen={onDecision} />
    {confirm && <ConfirmDialog title="Confirmer la synchronisation manuelle"
      confirmLabel="Synchroniser les changements applicables" close={() => setConfirm(false)}
      confirm={() => { studio.synchronize(); setConfirm(false); }}>
      <NJText>Appliquer la modification de surface de c1 et signaler les conflits c2 ({affectedNames('c2')}) et c6 ({affectedNames('c6')}).
        Conserver les corrections finales, les bâtiments, tous les abonnements, tracés et phasages.</NJText>
      <NJText>Les suppressions restent en attente d’arbitrage. Aucun recalcul automatique.</NJText>
    </ConfirmDialog>}
  </section>;
}
